(() => {
  const focusSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const focusable = (container) => [...container.querySelectorAll(focusSelector)].filter((node) => !node.closest('[hidden], [inert]') && node.getClientRects().length);
  const setVisible = (node, visible) => {
    node.hidden = !visible;
    node.inert = !visible;
  };
  const focus = (node) => node?.focus({ preventScroll: true });
  const easing = 'cubic-bezier(.2,.7,.2,1)';

  class SDAppleNavigation extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.header = this.closest('m-header');
      this.desktopHeader = this.closest('.m-header__desktop');
      this.nav = this.querySelector('[data-an-nav]');
      this.surface = this.querySelector('[data-an-surface]');
      this.backdrop = this.querySelector('[data-an-backdrop]');
      this.triggers = [...this.querySelectorAll('[data-an-trigger]')];
      this.panels = [...this.querySelectorAll('[data-an-panel]')];
      if (!this.nav || !this.surface || !this.backdrop) return;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.hoverMedia = matchMedia('(hover: hover) and (pointer: fine)');
      this.animations = new Map();
      this.generation = 0;
      this.pointerInside = new Set();
      this.activeKey = null;
      this.isOpen = false;
      setVisible(this.surface, false);
      this.backdrop.hidden = true;
      this.panels.forEach((panel) => setVisible(panel, false));
      this.triggers.forEach((trigger) => {
        trigger.setAttribute('aria-expanded', 'false');
        trigger.addEventListener('pointerenter', (event) => {
          if (!this.hoverMedia.matches || event.pointerType === 'touch') return;
          clearTimeout(this.hoverTimer);
          clearTimeout(this.leaveTimer);
          this.hoverTimer = setTimeout(() => this.open(trigger.dataset.anTrigger, 'pointer'), 150);
        }, options);
        trigger.addEventListener('pointerleave', () => clearTimeout(this.hoverTimer), options);
        trigger.addEventListener('click', (event) => {
          clearTimeout(this.hoverTimer);
          if (this.isOpen && this.activeKey === trigger.dataset.anTrigger) this.close();
          else this.open(trigger.dataset.anTrigger, event.detail === 0 ? 'keyboard' : 'pointer');
        }, options);
        trigger.addEventListener('keydown', (event) => {
          if (event.key !== 'ArrowDown') return;
          event.preventDefault();
          this.open(trigger.dataset.anTrigger, 'keyboard');
          focus(focusable(this.activePanel)[0]);
        }, options);
      });
      [this.nav, this.surface].forEach((region) => {
        region.addEventListener('pointerenter', (event) => {
          if (event.pointerType === 'touch') return;
          this.pointerInside.add(region);
          clearTimeout(this.leaveTimer);
        }, options);
        region.addEventListener('pointerleave', () => {
          this.pointerInside.delete(region);
          clearTimeout(this.leaveTimer);
          this.leaveTimer = setTimeout(() => {
            if (!this.pointerInside.size && !(this.inputMode === 'keyboard' && this.contains(document.activeElement))) this.close();
          }, 200);
        }, options);
      });
      this.backdrop.addEventListener('click', () => this.close(), options);
      document.addEventListener('pointerdown', (event) => {
        this.inputMode = 'pointer';
        if (this.isOpen && !this.contains(event.target)) this.close();
      }, { ...options, capture: true });
      document.addEventListener('click', (event) => {
        if (event.target.closest('m-search-popup, [data-open-search-popup], .m-cart-icon-bubble')) this.close();
      }, { ...options, capture: true });
      document.addEventListener('keydown', (event) => {
        this.inputMode = 'keyboard';
        if (event.key === 'Escape' && this.isOpen) {
          event.preventDefault();
          this.close({ returnFocus: true });
        }
      }, options);
      this.addEventListener('focusout', () => {
        cancelAnimationFrame(this.focusFrame);
        this.focusFrame = requestAnimationFrame(() => {
          if (this.isOpen && !this.contains(document.activeElement) && (this.inputMode === 'keyboard' || !this.pointerInside.size)) this.close();
        });
      }, options);
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-an-panel] a[href]')) this.close();
      }, options);
      window.addEventListener('resize', () => {
        if (window.innerWidth < 1200) this.close({ immediate: true });
        else if (this.isOpen) {
          this.updatePosition();
          this.resizeSurface();
        }
      }, options);
      window.addEventListener('scroll', () => {
        if (this.isOpen) {
          this.updatePosition();
          this.resizeSurface();
        }
      }, { ...options, passive: true });
      document.addEventListener('shopify:section:unload', (event) => {
        if (event.target.contains(this)) this.disconnectedCallback();
      }, options);
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => {
          if (this.isOpen) {
            this.updatePosition();
            this.resizeSurface();
          }
        });
        this.panels.forEach((panel) => this.resizeObserver.observe(panel));
      }
    }

    animate(node, keyframes, duration) {
      this.animations.get(node)?.cancel();
      this.animations.delete(node);
      if (!duration || !node.animate) return Promise.resolve();
      const animation = node.animate(keyframes, { duration, easing, fill: 'none' });
      this.animations.set(node, animation);
      return animation.finished.catch(() => {}).then(() => {
        if (this.animations.get(node) === animation) this.animations.delete(node);
      });
    }

    updatePosition() {
      const bottom = Math.max(0, (this.desktopHeader || this.header || this).getBoundingClientRect().bottom);
      this.style.setProperty('--sd-an-header-bottom', `${bottom}px`);
      this.availableHeight = Math.max(120, window.innerHeight - bottom);
      this.style.setProperty('--sd-an-available-height', `${this.availableHeight}px`);
    }

    panelHeight() {
      return Math.min(Math.ceil(this.activePanel?.scrollHeight || 0), this.availableHeight || window.innerHeight);
    }

    resizeSurface() {
      if (!this.activePanel || this.animations.has(this.surface)) return;
      this.surface.style.height = `${this.panelHeight()}px`;
      this.surface.style.overflowY = this.activePanel.scrollHeight > this.panelHeight() ? 'auto' : 'hidden';
    }

    open(key, inputMode = 'pointer') {
      if (window.innerWidth < 1200) return;
      const panel = this.panels.find((item) => item.dataset.anPanel === key);
      const trigger = this.triggers.find((item) => item.dataset.anTrigger === key);
      if (!panel || !trigger) return;
      clearTimeout(this.leaveTimer);
      clearTimeout(this.hoverTimer);
      this.inputMode = inputMode;
      if (this.isOpen && this.activeKey === key) return;
      const token = ++this.generation;
      const wasVisible = !this.surface.hidden;
      const previousHeight = wasVisible ? this.surface.getBoundingClientRect().height : 0;
      const previousOpacity = wasVisible ? getComputedStyle(this.surface).opacity : '0';
      const backdropOpacity = !this.backdrop.hidden ? getComputedStyle(this.backdrop).opacity : '0';
      this.animations.forEach((animation) => animation.cancel());
      this.animations.clear();
      this.isOpen = true;
      this.activeKey = key;
      this.activePanel = panel;
      this.launcher = trigger;
      this.classList.add('is-open');
      this.header?.classList.add('sd-an-active');
      this.surface.dataset.state = 'open';
      this.backdrop.dataset.state = 'open';
      this.surface.hidden = false;
      this.surface.inert = false;
      this.backdrop.hidden = false;
      this.triggers.forEach((item) => item.setAttribute('aria-expanded', String(item === trigger)));
      this.panels.forEach((item) => setVisible(item, item === panel));
      this.updatePosition();
      const targetHeight = this.panelHeight();
      this.surface.style.height = `${targetHeight}px`;
      this.surface.style.overflowY = 'hidden';
      this.surface.scrollTop = 0;
      const duration = this.motion.matches ? 0 : wasVisible ? 240 : 260;
      this.animate(this.surface, [
        { height: `${previousHeight}px`, opacity: previousOpacity },
        { height: `${targetHeight}px`, opacity: '1' }
      ], duration).then(() => {
        if (token === this.generation && this.isOpen) this.resizeSurface();
      });
      this.animate(this.backdrop, [{ opacity: backdropOpacity }, { opacity: '1' }], duration);
      this.animate(panel, [
        { opacity: '0', transform: 'translateY(8px)' },
        { opacity: '1', transform: 'translateY(0)' }
      ], this.motion.matches ? 0 : wasVisible ? 180 : 260);
    }

    close({ returnFocus = false, immediate = false } = {}) {
      clearTimeout(this.hoverTimer);
      clearTimeout(this.leaveTimer);
      if (!this.isOpen && !immediate) return;
      const token = ++this.generation;
      const height = this.surface.getBoundingClientRect().height;
      const opacity = getComputedStyle(this.surface).opacity;
      const backdropOpacity = getComputedStyle(this.backdrop).opacity;
      this.animations.forEach((animation) => animation.cancel());
      this.animations.clear();
      this.isOpen = false;
      this.classList.remove('is-open');
      this.surface.dataset.state = 'closing';
      this.backdrop.dataset.state = 'closing';
      this.surface.inert = true;
      this.surface.style.overflowY = 'hidden';
      this.triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
      if (returnFocus) focus(this.launcher);
      const duration = immediate || this.motion.matches ? 0 : 160;
      this.animate(this.backdrop, [{ opacity: backdropOpacity }, { opacity: '0' }], duration);
      this.animate(this.surface, [
        { height: `${height}px`, opacity },
        { height: '0px', opacity: '0' }
      ], duration).then(() => {
        if (token !== this.generation || this.isOpen) return;
        this.surface.hidden = true;
        this.backdrop.hidden = true;
        this.surface.style.height = '';
        delete this.surface.dataset.state;
        delete this.backdrop.dataset.state;
        this.panels.forEach((panel) => setVisible(panel, false));
        this.header?.classList.remove('sd-an-active');
        this.activeKey = null;
        this.activePanel = null;
      });
    }

    disconnectedCallback() {
      if (!this.abort) return;
      ++this.generation;
      this.abort.abort();
      this.abort = null;
      clearTimeout(this.hoverTimer);
      clearTimeout(this.leaveTimer);
      cancelAnimationFrame(this.focusFrame);
      this.resizeObserver?.disconnect();
      this.animations?.forEach((animation) => animation.cancel());
      this.header?.classList.remove('sd-an-active');
    }
  }

  class SDAppleMenuDrawer extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.header = this.closest('m-header');
      this.dialog = this.querySelector('[data-an-dialog]');
      this.scroll = this.querySelector('[data-an-scroll]');
      this.titleElement = this.querySelector('.sd-an-mobile-title[data-an-title], .sd-an-mobile-toolbar [data-an-title], span[data-an-title]');
      this.back = this.querySelector('[data-an-back]');
      this.closeButton = this.querySelector('[data-an-close]');
      this.trigger = this.header?.querySelector('[data-sd-an-drawer-trigger]');
      this.screens = [...this.querySelectorAll('[data-an-screen]')];
      this.nextButtons = [...this.querySelectorAll('[data-an-next]')];
      if (!this.dialog || !this.scroll || !this.back || !this.titleElement) return;
      this.rootTitle = this.dataset.anRootTitle || this.titleElement.textContent;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.inertRecords = new Map();
      this.screenKey = 'root';
      this.rootScroll = 0;
      this.isOpen = false;
      this.dialog.tabIndex = -1;
      this.nextButtons.forEach((button) => {
        button.addEventListener('click', () => {
          this.rootScroll = this.scroll.scrollTop;
          this.categoryLauncher = button;
          this.showScreen(button.dataset.anNext);
        }, options);
      });
      this.back.addEventListener('click', () => this.showScreen('root'), options);
      this.closeButton?.addEventListener('click', () => this.close(), options);
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-an-screen] a[href]')) this.close();
      }, options);
      document.addEventListener('keydown', (event) => this.onKeydown(event), options);
      document.addEventListener('focusin', (event) => {
        if (this.isOpen && !this.dialog.contains(event.target)) focus(focusable(this.dialog)[0] || this.dialog);
      }, options);
      window.addEventListener('resize', () => {
        if (window.innerWidth >= 1200 && this.isOpen) this.close();
      }, options);
      document.addEventListener('shopify:section:unload', (event) => {
        if (event.target.contains(this)) this.disconnectedCallback();
      }, options);
      this.observer = new MutationObserver(() => this.syncOpen());
      this.observer.observe(this, { attributes: true, attributeFilter: ['class'] });
      this.showScreen('root', false);
      this.syncOpen(true);
    }

    showScreen(key, animate = true) {
      const screen = this.screens.find((item) => item.dataset.anScreen === key);
      if (!screen) return;
      this.screenAnimation?.cancel();
      this.screenAnimation = null;
      this.screens.forEach((item) => setVisible(item, item === screen));
      this.screenKey = key;
      this.dataset.anCurrentScreen = key;
      this.back.hidden = key === 'root';
      const button = this.nextButtons.find((item) => item.dataset.anNext === key);
      this.titleElement.textContent = key === 'root' ? this.rootTitle : screen.dataset.anTitle || button?.dataset.anLabel || button?.textContent.trim() || this.rootTitle;
      this.nextButtons.forEach((item) => item.setAttribute('aria-expanded', String(key === item.dataset.anNext)));
      this.scroll.scrollTop = key === 'root' ? this.rootScroll : 0;
      if (animate && this.isOpen && !this.motion.matches && screen.animate) {
        this.screenAnimation = screen.animate([
          { opacity: '0', transform: `translateX(${key === 'root' ? '-24px' : '24px'})` },
          { opacity: '1', transform: 'translateX(0)' }
        ], { duration: 220, easing });
      }
      if (animate && this.isOpen) focus(key === 'root' ? this.categoryLauncher || focusable(screen)[0] : this.back);
    }

    setBackgroundInert(enabled) {
      if (enabled) {
        document.querySelectorAll('main, footer, #MainContent, .shopify-section-group-footer-group').forEach((node) => {
          if (node.contains(this) || this.contains(node) || this.inertRecords.has(node)) return;
          this.inertRecords.set(node, node.inert);
          node.inert = true;
        });
      } else {
        this.inertRecords.forEach((previous, node) => { node.inert = previous; });
        this.inertRecords.clear();
      }
    }

    syncOpen(initial = false) {
      const open = this.classList.contains('open');
      if (open === this.isOpen && !initial) return;
      this.isOpen = open;
      this.dialog.inert = !open;
      this.dialog.setAttribute('aria-hidden', String(!open));
      this.trigger?.setAttribute('aria-expanded', String(open));
      cancelAnimationFrame(this.focusFrame);
      if (open) {
        this.returnFocus = document.activeElement;
        this.rootScroll = 0;
        this.showScreen('root', false);
        this.setBackgroundInert(true);
        this.focusFrame = requestAnimationFrame(() => {
          if (this.isOpen) focus(focusable(this.dialog)[0] || this.dialog);
        });
      } else {
        this.screenAnimation?.cancel();
        this.setBackgroundInert(false);
        const active = document.activeElement;
        const restore = !initial && (this.contains(active) || active === document.body);
        if (restore) focus(this.trigger || (this.returnFocus?.isConnected ? this.returnFocus : null));
      }
    }

    onKeydown(event) {
      if (!this.isOpen) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        if (this.screenKey !== 'root') this.showScreen('root');
        else this.close();
      } else if (event.key === 'Tab') {
        const items = focusable(this.dialog);
        const first = items[0];
        const last = items[items.length - 1];
        const active = document.activeElement;
        if (!first) {
          event.preventDefault();
          focus(this.dialog);
        } else if (event.shiftKey && (active === first || active === this.dialog || !this.dialog.contains(active))) {
          event.preventDefault();
          focus(last);
        } else if (!event.shiftKey && (active === last || active === this.dialog || !this.dialog.contains(active))) {
          event.preventDefault();
          focus(first);
        }
      }
    }

    close() {
      if (this.header?.megamenu) this.header.megamenu.closeMenu();
      else {
        this.classList.remove('open');
        this.trigger?.classList.remove('active');
        this.header?.querySelector('.m-header__mobile')?.classList.remove('header-drawer-open');
        document.documentElement.classList.remove('prevent-scroll');
      }
    }

    disconnectedCallback() {
      if (!this.abort) return;
      this.abort.abort();
      this.abort = null;
      this.observer?.disconnect();
      this.screenAnimation?.cancel();
      cancelAnimationFrame(this.focusFrame);
      this.setBackgroundInert(false);
      if (this.isOpen) {
        document.documentElement.classList.remove('prevent-scroll');
        this.trigger?.classList.remove('active');
        this.trigger?.setAttribute('aria-expanded', 'false');
      }
    }
  }

  if (!customElements.get('sd-apple-navigation')) customElements.define('sd-apple-navigation', SDAppleNavigation);
  if (!customElements.get('sd-apple-menu-drawer')) customElements.define('sd-apple-menu-drawer', SDAppleMenuDrawer);
})();
